import React from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Icon, Tooltip, KeyValue, Row, Col } from '@folio/stripes-components';
import { Badge, Accordion, AccordionSet } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import ExportDropdown from './ExportDropdown';

const ExtractionResultsCard = ({ extractionResults, onRetry, onExport }) => {
  const renderArrayValue = (data, fallback = <FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.notSpecified" defaultMessage="Not specified" />) => {
    if (!data) return fallback;
    if (Array.isArray(data)) {
      if (data.length === 0) return fallback;
      return (
        <ul style={{ margin: 0, paddingRight: '20px', listStyle: 'disc' }}>
          {data.map((item, index) => <li key={index} style={{ marginBottom: '4px' }}>{item}</li>)}
        </ul>
      );
    }
    return data;
  };

  const { extractedData } = extractionResults;

  return (
    <Card
      cardStyle="positive"
      headerStart={<strong><FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.extractionResults" defaultMessage="Extraction Results" /></strong>}
      headerEnd={
        <div style={{ display: 'flex', gap: '8px' }}>
          <ExportDropdown extractionResults={extractionResults} onExport={onExport} />
          <Tooltip id="retry-extraction-tooltip" text={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.retryExtraction" defaultMessage="Retry Extraction" />}>
            {({ ref, ariaIds }) => (
              <Button ref={ref} aria-labelledby={ariaIds.text} buttonStyle="slim" onClick={onRetry} bottomMargin0>
                <Icon icon="refresh" size="large" />
              </Button>
            )}
          </Tooltip>
        </div>
      }
    >
      <div style={{ padding: '15px' }}>
        <AccordionSet>
          <Accordion
            label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.extractedBookInfo" defaultMessage="Extracted Book Information" />}
            id="book-info"
            open
          >
            <Row>
              <Col xs={12}>
                <KeyValue
                  label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.title" defaultMessage="Title" />}
                  value={extractedData.entities?.title || <FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.notSpecified" defaultMessage="Not specified" />}
                />
              </Col>
            </Row>
            {extractedData.entities?.subtitle && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.subtitle" defaultMessage="Subtitle" />} value={renderArrayValue(extractedData.entities.subtitle)} />
              </Col></Row>
            )}
            <Row><Col xs={12}>
              <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.author" defaultMessage="Author" />} value={renderArrayValue(extractedData.entities?.authors)} />
            </Col></Row>
            {extractedData.entities?.supervisors && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.supervisor" defaultMessage="Supervisor" />} value={renderArrayValue(extractedData.entities.supervisors)} />
              </Col></Row>
            )}
            <Row><Col xs={12}>
              <KeyValue
                label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.university" defaultMessage="University" />}
                value={extractedData.entities?.university || <FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.notSpecified" defaultMessage="Not specified" />}
              />
            </Col></Row>
            {extractedData.entities?.college && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.college" defaultMessage="College" />} value={extractedData.entities.college} />
              </Col></Row>
            )}
            {extractedData.entities?.department && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.department" defaultMessage="Department" />} value={extractedData.entities.department} />
              </Col></Row>
            )}
            {extractedData.entities?.country && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.country" defaultMessage="Country" />} value={extractedData.entities.country} />
              </Col></Row>
            )}
            {extractedData.entities?.publisher && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.publisher" defaultMessage="Publisher" />} value={extractedData.entities.publisher} />
              </Col></Row>
            )}
            {extractedData.entities?.language && (
              <Row><Col xs={12}>
                <KeyValue label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.language" defaultMessage="Language" />} value={renderArrayValue(extractedData.entities.language)} />
              </Col></Row>
            )}
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.publicationYear" defaultMessage="Publication Year" />}
                  value={extractedData.entities?.publication_year || <FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.notSpecified" defaultMessage="Not specified" />}
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.academicDegree" defaultMessage="Academic Degree" />}
                  value={extractedData.entities?.degree || extractedData.entities?.other_entities?.additional_info?.[0] || <FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.notSpecified" defaultMessage="Not specified" />}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.confidence" defaultMessage="Confidence" />}
                  value={<Badge color="success">{Math.round((extractedData.metadata?.confidence || 0) * 100)}%</Badge>}
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.processingTime" defaultMessage="Processing Time" />}
                  value={`${Math.round((extractedData.metadata?.processingTime || 0) / 1000)}s`}
                />
              </Col>
            </Row>
          </Accordion>

          <Accordion
            label={<FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.fullExtractedText" defaultMessage="Full Extracted Text" />}
            id="raw-text"
          >
            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.5', maxHeight: '300px', overflowY: 'auto', whiteSpace: 'pre-wrap', direction: 'rtl' }}>
              {extractedData.extractedText || <FormattedMessage id="ui-plugin-inventory-templates.imageExtraction.noExtractedText" defaultMessage="No extracted text" />}
            </div>
          </Accordion>
        </AccordionSet>
      </div>
    </Card>
  );
};

ExtractionResultsCard.propTypes = {
  extractionResults: PropTypes.object.isRequired,
  onRetry: PropTypes.func.isRequired,
  onExport: PropTypes.func,
};

export default ExtractionResultsCard;
